import { Link, Outlet } from "react-router-dom";
import { useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import EmailVerifyBanner from "./EmailVerifyBanner";
import {
  Activity,
  Calculator,
  LineChart,
  User as UserIcon,
  BookOpen,
  Trophy,
  Layers,
  CreditCard,
  Terminal as TerminalIcon,
  Shield,
} from "lucide-react";
import { DesktopNav, MobileNav, UserControls } from "./layout/HeaderParts";

const baseNavLinks = [
  { to: "/app", label: "Dashboard", icon: Activity, end: true },
  { to: "/app/terminal", label: "Terminal", icon: TerminalIcon },
  { to: "/app/calculator", label: "Calc", icon: Calculator },
  { to: "/app/strategy", label: "Strategy", icon: BookOpen },
  { to: "/app/arb", label: "Arb", icon: Layers },
  { to: "/app/tracker", label: "Tracker", icon: LineChart },
  { to: "/app/track-record", label: "Record", icon: Trophy },
  { to: "/app/billing", label: "Billing", icon: CreditCard },
  { to: "/app/profile", label: "Profile", icon: UserIcon },
];

function Brand() {
  return (
    <Link to="/app" className="flex items-center gap-3" data-testid="brand-link">
      <div className="w-8 h-8 border border-[#00D4FF] rotate-45 flex items-center justify-center">
        <span className="-rotate-45 text-[#00D4FF] font-mono text-xs font-bold">M</span>
      </div>
      <div>
        <div className="font-display text-lg leading-none">Martingale</div>
        <div className="overline leading-none mt-1">PLAYBOOK·TERMINAL</div>
      </div>
    </Link>
  );
}

function Footer() {
  return (
    <footer className="relative z-10 border-t border-zinc-800 mt-20 py-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row justify-between gap-4 text-xs mono text-zinc-500">
        <div>MARTINGALE PLAYBOOK · DATA FROM ESPN PUBLIC FEEDS · AI BY CLAUDE SONNET 4.5</div>
        <div className="flex flex-wrap gap-3">
          <Link to="/terms" className="hover:text-[#00D4FF]">Terms</Link>
          <Link to="/privacy" className="hover:text-[#00D4FF]">Privacy</Link>
          <Link to="/responsible-gambling" className="hover:text-[#00D4FF]">Responsible Gambling</Link>
          <Link to="/contact" className="hover:text-[#00D4FF]">Contact</Link>
        </div>
        <div className="amber-text">⚠ 21+ · GAMBLE RESPONSIBLY · 1-800-GAMBLER</div>
      </div>
    </footer>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();

  const navLinks = useMemo(() => {
    if (user?.founder) {
      return [
        ...baseNavLinks,
        { to: "/app/admin", label: "Admin", icon: Shield },
      ];
    }
    return baseNavLinks;
  }, [user?.founder]);

  return (
    <div className="min-h-screen bg-background grain relative">
      <header
        className="sticky top-0 z-30 bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800"
        data-testid="app-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Brand />
          <DesktopNav navLinks={navLinks} />
          <UserControls user={user} onLogout={logout} />
        </div>
        <MobileNav navLinks={navLinks} />
      </header>

      <EmailVerifyBanner />

      <main className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
}
